import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Box, Stack, TextInput } from "@mantine/core";
import React, { useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import { useAuth } from "../../auth/AuthProvider";
import { NavTreeItem, SideNavTree } from "../../components/common/navigation";
import { ROUTES } from "../../constants/routes";
import { useTranslation } from "../../hooks/useTranslation";
import { FAQ_TOPICS } from "./config/faqTopics";
import { filterFaqNavTree } from "./utils/filterFaqNavTree";

const FAQ_NAV_STORAGE_KEY = "cv-portal:faq-nav-collapsed";

// Generic FAQ shell — search + SideNavTree + Outlet. Has no knowledge of Profile
// Completion or any other specific topic; every topic contributes its own root
// NavTreeItem via useTopicTree(). Adding a topic never requires changing this file.
export const Faq: React.FC = () => {
    const { userRole } = useAuth();
    const { t } = useTranslation();
    const [query, setQuery] = useState("");

    const topicItems = FAQ_TOPICS.map((topic) => topic.useTopicTree()).filter(
        (item): item is NavTreeItem => item !== null
    );

    const tree = useMemo(() => filterFaqNavTree(topicItems, query), [topicItems, query]);

    return (
        <Box style={{ display: "flex", gap: "var(--mantine-spacing-md)", alignItems: "flex-start" }}>
            <Stack gap="xs" style={{ flexShrink: 0 }}>
                <TextInput
                    leftSection={<MagnifyingGlassIcon width={15} />}
                    value={query}
                    onChange={(event) => setQuery(event.currentTarget.value)}
                    placeholder={t("FAQ_SEARCH_PLACEHOLDER", undefined, "Search FAQ")}
                />
                <SideNavTree
                    items={tree}
                    basePath={ROUTES.FAQ}
                    userRole={userRole}
                    storageKey={FAQ_NAV_STORAGE_KEY}
                />
            </Stack>
            <Box style={{ flex: 1, minWidth: 0 }}>
                <Outlet />
            </Box>
        </Box>
    );
};
